import { Trans, useLingui } from '@lingui/react/macro';
import ErrorBoundary from '@web/components/ErrorBoundary';
import { useCurrentEntity } from '@web/hooks/use-current-entity';
import Typography from 'carbon-react/lib/components/typography';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import Dialog from '../Shared/Dialog';
import AuditDetails from './AuditDetails';
import { ActiveAudit, AuditContext, AuditContextType } from './hooks/useAudit';

const ErrorContainer = styled.div`
  padding-top: ${(props) => props.theme.space[3]};
  flex: 1;
  min-height: 490px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.space[3]};
`;

interface AuditProviderProps {
  children: React.ReactNode;
}

function AuditProvider({ children }: AuditProviderProps) {
  const { t } = useLingui();
  const [audit, setAudit] = useState<ActiveAudit | null>(null);
  const companyId = useCurrentEntity()?.id;

  const isOpen = !!companyId && (audit?.visible ?? false);

  const handleClose = () => {
    setAudit((c) => (c ? { ...c, visible: false } : c));
  };

  const providerValue = useMemo<AuditContextType>(
    () => ({
      active: audit,
      loadAudit(audit) {
        setAudit({
          ...audit,
          visible: true,
        });
      },
    }),
    [audit],
  );

  return (
    <AuditContext.Provider value={providerValue}>
      <Dialog
        open={isOpen}
        complexForm
        title={t`View audit trail`}
        size="large"
        onCancel={() => setAudit((c) => (c ? { ...c, visible: false } : c))}
      >
        <ErrorBoundary fallbackUI={(error) => <ErrorFallback error={error} />}>
          {audit && companyId && <AuditDetails companyId={companyId} audit={audit} onClose={handleClose} />}
        </ErrorBoundary>
      </Dialog>
      {children}
    </AuditContext.Provider>
  );
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <ErrorContainer>
      <Typography variant="segment-header">
        <Trans>Something went wrong</Trans>
      </Typography>

      <Typography variant="segment-subheader">{error.message}</Typography>
      <Typography variant="strong">
        <Trans>Please try again. If the error persists, please contact support.</Trans>
      </Typography>
    </ErrorContainer>
  );
}

export default AuditProvider;
